// A collection's mark, as one URL, by the site's own rules.
//
// Collection records point `icon` wherever the collection was registered from:
// our API host's `/inscription/preview/` (which only the CDN serves, so it
// 404s there), a third-party ord mirror that can refuse to be hotlinked, or a
// plain upload. A few collections are served from assets we ship instead.
// `CollectionIcon` has always applied these rules when rendering a collection;
// this is the same decision as a value, for the surfaces that need the URL
// itself (the social feed's market pills, the market picker).

import { CDN_PREVIEW_URL, CDN_URL, COLLECTION_IMAGE_OVERRIDES, COLLECTION_SLUGS } from '@/utils/constants'
import { keepsOriginalInscriptionArt } from '@/utils/inscription-content-url'

/// Slug → the image we serve ourselves, ahead of whatever the API says.
export const COLLECTION_ICON_OVERRIDES: Record<string, string> = {
	...COLLECTION_IMAGE_OVERRIDES,
	runestone: '/collections/runestone/icon.webp',
	'bitcoin-puppets': '/collections/bitcoin-puppets/icon.avif'
}

/// An inscription id sitting in an icon URL's path, whatever host serves it.
const INSCRIPTION_IN_URL = /\/(?:inscription\/)?(?:preview|content)\/([0-9a-fA-F]{64}i\d+)/

export interface IconSource {
	slug?: string | null
	icon?: string | null
	icon_inscription?: { id?: string | null; content_type?: string | null } | null
}

export default function collectionIconUrl(collection: IconSource): string | null {
	const slug = collection.slug?.trim() ?? ''
	const override = COLLECTION_ICON_OVERRIDES[slug]
	if (override) return override
	if (slug && COLLECTION_SLUGS.includes(slug)) return `/collections/${slug}/icon.png`

	const icon = collection.icon?.trim()
	// An icon that IS an inscription is read from our own CDN by its id — the
	// host the record happens to name may 404 or refuse us.
	const inscription = INSCRIPTION_IN_URL.exec(icon ?? '')?.[1]
	if (inscription) return `${CDN_URL}/${inscription}`
	if (icon) return icon

	// Fall back to the collection's own inscription, read as CONTENT: a
	// rendered preview is not generated for every inscription (some 404),
	// while the content itself is always there. Text inscriptions — rune
	// tickers write their name — are not images, so they get no mark at all
	// rather than a request that cannot paint.
	const fallback = collection.icon_inscription
	const id = fallback?.id?.trim()
	if (!id || fallback?.content_type?.toLowerCase().includes('text')) return null
	return `${CDN_URL}/${id}`
}

const INSCRIPTION_PATH = /\/(?:content|preview)\/([0-9a-fA-F]{64}i\d+)/

/// Rail and card marks. Full `/content/` inscriptions are often several
/// megabytes; the preview endpoint is a small raster of the same id.
export function collectionThumbnailUrl(
	src: string | null | undefined,
	inscriptionId?: string | null,
	options?: { contentType?: string | null; slug?: string | null }
): string | null {
	if (src?.startsWith('/')) return src
	const fromSrc = src ? INSCRIPTION_PATH.exec(src)?.[1] : undefined
	const id = fromSrc || inscriptionId?.trim() || ''
	if (
		id &&
		(!src || /\/(?:content|preview)\//.test(src)) &&
		!keepsOriginalInscriptionArt(options?.contentType, options?.slug)
	) {
		return `${CDN_PREVIEW_URL}/${id}`
	}
	return src?.trim() || null
}
